import type {
  ActionEnvelope,
  ActionType,
  ChatState,
  Message,
  PendingMessage,
  PendingMessageKind,
} from '@microsoft/agent-host-protocol'

const ACTION_PENDING_MESSAGE_SET = 'chat/pendingMessageSet' as ActionType.ChatPendingMessageSet
const ACTION_PENDING_MESSAGE_REMOVED = 'chat/pendingMessageRemoved' as ActionType.ChatPendingMessageRemoved
const ACTION_TURN_STARTED = 'chat/turnStarted' as ActionType.ChatTurnStarted
const ACTION_RESPONSE_PART = 'chat/responsePart' as ActionType.ChatResponsePart
const ACTION_TOOL_CALL_START = 'chat/toolCallStart' as ActionType.ChatToolCallStart
const PENDING_MESSAGE_KIND_STEERING = 'steering' as PendingMessageKind.Steering

export interface RelaySteeringMessage extends PendingMessage {
  turnId?: string
  responsePartIndex: number
  status: 'pending' | 'handed-off' | 'superseded'
}

function captureInsertionPoint(
  state: ChatState | undefined,
): Pick<RelaySteeringMessage, 'turnId' | 'responsePartIndex'> {
  const activeTurn = state?.activeTurn
  const responsePartIndex = activeTurn?.responseParts.length ?? 0

  return {
    ...(activeTurn ? {turnId: activeTurn.id} : {}),
    responsePartIndex,
  }
}

export function recordRelaySteeringMessage(
  messages: readonly RelaySteeringMessage[],
  pendingMessage: PendingMessage,
  state: ChatState | undefined,
): readonly RelaySteeringMessage[] {
  const insertionPoint = captureInsertionPoint(state)
  let changed = false
  const backfilledMessages = messages.map(message => {
    if (message.id !== pendingMessage.id || message.turnId || !insertionPoint.turnId) return message
    changed = true
    return {
      ...message,
      turnId: insertionPoint.turnId,
    }
  })
  if (changed) return backfilledMessages
  if (messages.some(message => message.id === pendingMessage.id)) return messages

  return [
    ...messages.map(message => (message.status === 'pending' ? {...message, status: 'superseded' as const} : message)),
    {
      ...pendingMessage,
      ...insertionPoint,
      status: 'pending',
    },
  ]
}

export function reconcileRelaySteeringSnapshot(
  messages: readonly RelaySteeringMessage[],
  state: ChatState,
): readonly RelaySteeringMessage[] {
  const current = state.steeringMessage
  let next = messages

  if (current) {
    next = recordRelaySteeringMessage(messages, current, state)
  }

  let changed = next !== messages
  const reconciled = next.map(message => {
    const status: RelaySteeringMessage['status'] = getReconciledSteeringStatus(message, state)
    const responsePartIndex = getReconciledResponsePartIndex(message, state)
    if (message.status === status && message.responsePartIndex === responsePartIndex) return message
    changed = true
    return {...message, status, responsePartIndex}
  })

  return changed ? reconciled : messages
}

function getReconciledSteeringStatus(message: RelaySteeringMessage, state: ChatState): RelaySteeringMessage['status'] {
  if (message.id === state.steeringMessage?.id) return 'pending'
  if (message.status === 'handed-off') return message.status
  if (message.turnId && state.activeTurn && message.turnId !== state.activeTurn.id) return 'handed-off'
  return message.status
}

function getReconciledResponsePartIndex(message: RelaySteeringMessage, state: ChatState): number {
  if (message.status === 'handed-off' || !message.turnId || message.turnId !== state.activeTurn?.id) {
    return message.responsePartIndex
  }
  return Math.max(message.responsePartIndex, state.activeTurn.responseParts.length)
}

function hasSameSerializableValue(left: unknown, right: unknown): boolean {
  if (left === right) return true
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => hasSameSerializableValue(value, right[index]))
    )
  }
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false

  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const leftKeys = Object.keys(leftRecord)
    .filter(key => key !== '_meta')
    .sort()
  const rightKeys = Object.keys(rightRecord)
    .filter(key => key !== '_meta')
    .sort()
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) => key === rightKeys[index] && hasSameSerializableValue(leftRecord[key], rightRecord[key]),
    )
  )
}

function isSameSubmittedMessage(left: Message, right: Message): boolean {
  return (
    left.text === right.text &&
    left.origin.kind === right.origin.kind &&
    hasSameSerializableValue(left.attachments ?? [], right.attachments ?? []) &&
    (left.model === undefined || hasSameSerializableValue(left.model, right.model)) &&
    (left.agent === undefined || hasSameSerializableValue(left.agent, right.agent))
  )
}

function reconcileSteeringMessagesForStartedTurn(
  messages: readonly RelaySteeringMessage[],
  activeTurnId: string,
  turnMessage?: Message,
): readonly RelaySteeringMessage[] {
  let canonicalMessageIndex = -1
  for (let index = messages.length - 1; turnMessage && index >= 0; index--) {
    const message = messages[index]
    if (message && message.turnId !== activeTurnId && isSameSubmittedMessage(message.message, turnMessage)) {
      canonicalMessageIndex = index
      break
    }
  }

  let changed = false
  const next = messages.flatMap((message, index) => {
    if (index === canonicalMessageIndex) {
      changed = true
      return []
    }
    if (!message.turnId || message.turnId === activeTurnId) return [message]
    // Later submissions belong after the canonical message that started this turn.
    const followsCanonicalMessage = canonicalMessageIndex >= 0 && index > canonicalMessageIndex
    if (message.status === 'handed-off' && !followsCanonicalMessage) return [message]
    changed = true
    return [
      {
        ...message,
        ...(followsCanonicalMessage ? {turnId: activeTurnId, responsePartIndex: 0} : {}),
        status: 'handed-off' as const,
      },
    ]
  })
  return changed ? next : messages
}

function updateRemovedSteeringMessage(
  messages: readonly RelaySteeringMessage[],
  id: string,
  handedOff: boolean,
): readonly RelaySteeringMessage[] {
  let changed = false
  const next = messages.map(message => {
    if (message.id !== id || message.status === 'handed-off') return message
    const status: RelaySteeringMessage['status'] = handedOff ? 'handed-off' : 'superseded'
    if (message.status === status) return message
    changed = true
    return {...message, status}
  })
  return changed ? next : messages
}

function movePendingSteeringAfterNewResponsePart(
  messages: readonly RelaySteeringMessage[],
  state: ChatState | undefined,
  turnId: string,
): readonly RelaySteeringMessage[] {
  if (state?.activeTurn?.id !== turnId) return messages
  const nextResponsePartIndex = state.activeTurn.responseParts.length + 1
  let changed = false
  const next = messages.map(message => {
    if (
      message.status === 'handed-off' ||
      message.turnId !== turnId ||
      message.responsePartIndex >= nextResponsePartIndex
    ) {
      return message
    }
    changed = true
    return {...message, responsePartIndex: nextResponsePartIndex}
  })
  return changed ? next : messages
}

export function reduceRelaySteeringAction(
  messages: readonly RelaySteeringMessage[],
  envelope: ActionEnvelope,
  state: ChatState | undefined,
): readonly RelaySteeringMessage[] {
  if (envelope.rejectionReason) return messages
  const {action} = envelope

  if (action.type === ACTION_PENDING_MESSAGE_SET && action.kind === PENDING_MESSAGE_KIND_STEERING) {
    return recordRelaySteeringMessage(messages, {id: action.id, message: action.message}, state)
  }

  if (action.type === ACTION_TURN_STARTED) {
    return reconcileSteeringMessagesForStartedTurn(
      messages,
      action.turnId,
      action.queuedMessageId ? undefined : action.message,
    )
  }

  if (action.type === ACTION_RESPONSE_PART || action.type === ACTION_TOOL_CALL_START) {
    return movePendingSteeringAfterNewResponsePart(messages, state, action.turnId)
  }

  if (action.type !== ACTION_PENDING_MESSAGE_REMOVED || action.kind !== PENDING_MESSAGE_KIND_STEERING) {
    return messages
  }

  return updateRemovedSteeringMessage(messages, action.id, envelope.origin === undefined)
}
